import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarDays, Check, ChevronDown, Info } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { format } from "date-fns";
import { ar, enUS } from "date-fns/locale";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface ScheduledDaySelectProps {
  value: number[];
  campaignDates: Date[];
  onChange: (days: number[]) => void;
  /** Days already taken by other creatives in the same location */
  takenDays?: number[];
  /** Visual density preset for embedding inside cards/previews */
  size?: "sm" | "md";
  className?: string;
}

const ScheduledDaySelect = ({
  value,
  campaignDates,
  onChange,
  takenDays = [],
  size = "md",
  className,
}: ScheduledDaySelectProps) => {
  const { language } = useLanguage();
  const locale = language === "ar" ? ar : enUS;
  const isRtl = language === "ar";
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuRect, setMenuRect] = useState<{ top: number; left: number; width: number } | null>(null);

  const updateMenuPosition = () => {
    const el = triggerRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const width = Math.max(rect.width, 180);
    const left = isRtl ? rect.right - width : rect.left;
    // Use absolute positioning with scroll offset so it stays with the content
    setMenuRect({ 
      top: rect.bottom + window.scrollY + 6, 
      left: left + window.scrollX, 
      width 
    });
  };

  useEffect(() => {
    if (!isOpen) return;

    updateMenuPosition();

    const handleResize = () => setIsOpen(false);
    window.addEventListener("resize", handleResize);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handleMouseDown);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [isOpen, isRtl]);

  const sizeStyles = {
    sm: {
      wrapper: "gap-2",
      label: "text-xs",
      container: "gap-3 p-2.5",
      badge: "px-2.5 py-1 text-xs",
      trigger: "h-9 text-sm px-3",
      icon: "h-4 w-4",
      chevron: "h-4 w-4",
      infoIcon: "h-3.5 w-3.5",
      checkbox: "h-4 w-4",
      optionText: "text-sm",
    },
    md: {
      wrapper: "gap-2.5",
      label: "text-sm",
      container: "gap-3 p-3",
      badge: "px-3 py-1.5 text-sm",
      trigger: "h-10 text-sm px-4",
      icon: "h-5 w-5",
      chevron: "h-4 w-4",
      infoIcon: "h-4 w-4",
      checkbox: "h-5 w-5",
      optionText: "text-sm",
    },
  };

  const styles = sizeStyles[size];

  const labelText = isRtl ? "أيام عرض هذا المحتوى" : "Show this creative on";
  const tooltipText = isRtl 
    ? "اختر الأيام التي سيتم فيها عرض هذا المحتوى الإعلاني خلال فترة الحملة"
    : "Choose which days of your campaign this creative will be displayed on the screens";

  const toggleDay = (day: number) => {
    // Don't allow selecting taken days (unless it's already ours)
    if (takenDays.includes(day) && !value.includes(day)) return;
    
    if (value.includes(day)) {
      // Allow deselecting any day, including the last one
      onChange(value.filter(d => d !== day).sort((a, b) => a - b));
    } else {
      onChange([...value, day].sort((a, b) => a - b));
    }
  };

  const selectAll = () => {
    // Select all days that aren't taken by others
    const availableDays = campaignDates
      .map((_, idx) => idx + 1)
      .filter(day => !takenDays.includes(day) || value.includes(day));
    onChange(availableDays);
  };

  const selectedDaysLabel = value.length === 0
    ? (isRtl ? "لم يتم الاختيار" : "None")
    : value.length === campaignDates.length
      ? (isRtl ? "كل الأيام" : "All days")
      : value.length === 1
        ? (isRtl ? `اليوم ${value[0]}` : `Day ${value[0]}`)
        : (isRtl ? `${value.length} أيام` : `${value.length} days`);
  
  const badgeClass = value.length === 0
    ? "bg-primary/80 text-primary-foreground"
    : "bg-primary text-primary-foreground";

  return (
    <div className={`flex flex-col ${styles.wrapper} ${className ?? ""}`}>
      {/* Label with tooltip */}
      <div className="flex items-center gap-2">
        <CalendarDays className={`${styles.icon} text-primary`} />
        <span className={`${styles.label} font-semibold text-foreground`}>
          {labelText}
        </span>
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" className="inline-flex items-center justify-center">
                <Info className={`${styles.infoIcon} text-muted-foreground/70 hover:text-primary transition-colors`} />
              </button>
            </TooltipTrigger>
            <TooltipContent 
              side="top" 
              className="max-w-[220px] text-center bg-popover border border-border shadow-xl z-[100]"
            >
              <p className="text-xs leading-relaxed">{tooltipText}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Selector row */}
      <div
        className={`
          flex items-center ${styles.container} 
          rounded-xl border-2 border-border/80 bg-card shadow-sm
        `.trim()}
      >
        {/* Days badge */}
        <div
          className={`
            ${styles.badge} rounded-lg font-bold
            whitespace-nowrap shrink-0 shadow-sm
            ${badgeClass}
          `}
        >
          {selectedDaysLabel}
        </div>

        {/* Divider */}
        <div className="h-6 w-px bg-border/60 shrink-0" />

        {/* Dropdown trigger */}
        <div className="relative flex-1 min-w-0">
          <button
            ref={triggerRef}
            type="button"
            onClick={() => {
              setIsOpen((prev) => {
                const next = !prev;
                if (!prev && next) requestAnimationFrame(updateMenuPosition);
                return next;
              });
            }}
            className={`
              w-full flex items-center justify-between rounded-lg border-2 border-border/80
              bg-background hover:bg-accent/60 hover:border-primary/50
              text-foreground font-semibold
              focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary
              transition-all cursor-pointer shadow-sm
              ${styles.trigger}
            `}
            aria-label={isRtl ? "اختر أيام العرض" : "Select display days"}
            aria-expanded={isOpen}
          >
            <span className="truncate">
              {isRtl ? "اختر الأيام..." : "Select days..."}
            </span>
            <ChevronDown
              className={`
                ${styles.chevron} text-muted-foreground shrink-0 ml-2
                transition-transform duration-200 ${isOpen ? "rotate-180" : ""}
              `}
            />
          </button>

          {/* Dropdown menu (ported to body to avoid overflow clipping) */}
          {isOpen && menuRect && typeof document !== "undefined" &&
            createPortal(
              <div
                ref={menuRef}
                style={{ top: menuRect.top, left: menuRect.left, width: Math.max(menuRect.width, 220) }}
                className="absolute bg-background text-foreground border-2 border-border rounded-xl shadow-2xl z-[9999] py-2 max-h-[280px] overflow-y-auto"
              >
                {/* Select All option */}
                {campaignDates.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={selectAll}
                      className={`
                        w-full flex items-center gap-3 px-4 py-3
                        hover:bg-accent transition-colors text-left
                        ${value.length === campaignDates.length ? "bg-accent/50" : ""}
                      `}
                    >
                      <div
                        className={`
                          ${styles.checkbox} rounded-md border-2 flex items-center justify-center shrink-0 transition-all
                          ${value.length === campaignDates.length
                            ? "bg-primary border-primary text-primary-foreground shadow-sm"
                            : "border-muted-foreground/50 hover:border-primary/50"
                          }
                        `}
                      >
                        {value.length === campaignDates.length && (
                          <Check className="h-3.5 w-3.5" />
                        )}
                      </div>
                      <span className={`${styles.optionText} font-semibold`}>
                        {isRtl ? "اختر الكل" : "Select all"}
                      </span>
                    </button>
                    <div className="h-px bg-border mx-3 my-1.5" />
                  </>
                )}

                {campaignDates.length === 0 && (
                  <div className="px-4 py-3 text-sm text-muted-foreground">
                    {isRtl ? "لا توجد أيام للحملة" : "No campaign days found"}
                  </div>
                )}

                {/* Individual day options */}
                {campaignDates.map((date, idx) => {
                  const dayNumber = idx + 1;
                  const isSelected = value.includes(dayNumber);
                  const isTaken = takenDays.includes(dayNumber) && !isSelected;
                  const dayLabel = isRtl ? `اليوم ${dayNumber}` : `Day ${dayNumber}`;

                  let dateLabel = "";
                  try {
                    dateLabel = format(date, "EEE, MMM d", { locale });
                  } catch {
                    dateLabel = "";
                  }

                  return (
                    <button
                      key={dayNumber}
                      type="button"
                      onClick={() => toggleDay(dayNumber)}
                      disabled={isTaken}
                      className={`
                        w-full flex items-center gap-3 px-4 py-3
                        transition-colors text-left
                        ${isTaken 
                          ? "opacity-50 cursor-not-allowed bg-muted/50" 
                          : "hover:bg-accent"
                        }
                        ${isSelected ? "bg-accent/50" : ""}
                      `}
                    >
                      <div
                        className={`
                          ${styles.checkbox} rounded-md border-2 flex items-center justify-center shrink-0 transition-all
                          ${isSelected
                            ? "bg-primary border-primary text-primary-foreground shadow-sm"
                            : isTaken
                              ? "bg-muted border-muted-foreground/30"
                              : "border-muted-foreground/50 hover:border-primary/50"
                          }
                        `}
                      >
                        {isSelected && <Check className="h-3.5 w-3.5" />}
                      </div>
                      <div className="flex flex-col min-w-0 gap-0.5">
                        <span className={`${styles.optionText} font-semibold ${isTaken ? "text-muted-foreground" : ""}`}>
                          {dayLabel}
                          {isTaken && (
                            <span className="ml-2 text-xs text-muted-foreground/80 font-normal">
                              ({isRtl ? "مستخدم" : "taken"})
                            </span>
                          )}
                        </span>
                        {dateLabel ? (
                          <span className="text-xs text-muted-foreground">
                            {dateLabel}
                          </span>
                        ) : null}
                      </div>
                    </button>
                  );
                })}
              </div>,
              document.body
            )}
        </div>
      </div>
    </div>
  );
};

export default ScheduledDaySelect;
